import { type FormEvent, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  useBulkImport,
  useCreateEntry,
  useDeleteEntry,
  useDeleteLanguage,
  useEntries,
  useLanguage,
  useStats,
  useUpdateEntry,
  useUpdateLanguage,
} from '../api/hooks'
import type { Entry } from '../api/types'
import { Button, Card, EmptyState, ErrorText, Input, Spinner } from '../components/ui'

export default function LanguageDetail() {
  const languageId = Number(useParams().id)
  const navigate = useNavigate()

  const { data: language, isPending, error } = useLanguage(languageId)
  const { data: stats } = useStats(languageId)
  const [search, setSearch] = useState('')
  const { data: page } = useEntries(languageId, search)
  const [importing, setImporting] = useState(false)

  const updateLanguage = useUpdateLanguage(languageId)
  const deleteLanguage = useDeleteLanguage()

  if (isPending) return <Spinner />
  if (error) return <ErrorText error={error} />
  if (!language) return null

  const due = (stats?.due_now ?? 0) + (stats?.new_cards ?? 0)

  return (
    <div className="space-y-6">
      {/* --- header ------------------------------------------------------ */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{language.name}</h1>
          <p className="text-sm text-muted">
            {language.source_language} → {language.name}
            {language.description && ` · ${language.description}`}
          </p>
        </div>

        {language.is_owner && (
          <div className="flex flex-wrap gap-2">
            <Link to={`/languages/${languageId}/practice`}>
              <Button disabled={language.entry_count === 0}>
                Practice{due > 0 ? ` (${due})` : ''}
              </Button>
            </Link>
            <Button
              variant="secondary"
              onClick={() =>
                updateLanguage.mutate({
                  visibility: language.visibility === 'public' ? 'private' : 'public',
                })
              }
            >
              {language.visibility === 'public' ? 'Unpublish' : 'Publish'}
            </Button>
            <a href={`/api/languages/${languageId}/export.csv`}>
              <Button variant="secondary">Export CSV</Button>
            </a>
            <Button
              variant="danger"
              onClick={() => {
                if (!confirm(`Delete "${language.name}" and all its words?`)) return
                deleteLanguage.mutate(languageId, { onSuccess: () => navigate('/') })
              }}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {stats && <StatsRow stats={stats} />}

      {/* --- word list --------------------------------------------------- */}
      {language.is_owner && (
        <>
          <AddEntryForm languageId={languageId} />

          <div className="flex items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search words…"
              className="max-w-xs"
            />
            <Button variant="ghost" onClick={() => setImporting((open) => !open)}>
              {importing ? 'Close import' : 'Bulk import'}
            </Button>
          </div>

          {importing && (
            <BulkImportForm languageId={languageId} onDone={() => setImporting(false)} />
          )}
        </>
      )}

      {!page ? (
        <Spinner />
      ) : page.items.length === 0 ? (
        <EmptyState
          title={search ? 'No matches' : 'No words yet'}
          hint={
            search
              ? 'Try a different search.'
              : 'Add your first word above, or paste a whole list with Bulk import.'
          }
        />
      ) : (
        <EntryTable
          entries={page.items}
          total={page.total}
          languageId={languageId}
          editable={language.is_owner}
          sourceLabel={language.source_language}
          targetLabel={language.name}
        />
      )}
    </div>
  )
}

function StatsRow({
  stats,
}: {
  stats: { due_now: number; new_cards: number; learned: number; streak_days: number }
}) {
  const tiles = [
    { label: 'Due now', value: stats.due_now },
    { label: 'New', value: stats.new_cards },
    { label: 'Learned', value: stats.learned },
    { label: 'Day streak', value: stats.streak_days },
  ]
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {tiles.map((tile) => (
        <Card key={tile.label} className="py-4">
          <dt className="text-xs font-medium tracking-wide text-muted uppercase">
            {tile.label}
          </dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums">{tile.value}</dd>
        </Card>
      ))}
    </dl>
  )
}

function AddEntryForm({ languageId }: { languageId: number }) {
  const create = useCreateEntry(languageId)
  const [source, setSource] = useState('')
  const [target, setTarget] = useState('')

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    create.mutate(
      { source_text: source, target_text: target },
      {
        onSuccess: () => {
          setSource('')
          setTarget('')
          // Focus stays in the first field so a whole list can be typed
          // without ever reaching for the mouse.
          document.getElementById('new-source')?.focus()
        },
      },
    )
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
        <Input
          id="new-source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="house"
          required
          className="min-w-40 flex-1"
        />
        <span className="pb-2 text-muted">→</span>
        <Input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="domus"
          required
          className="min-w-40 flex-1"
        />
        <Button type="submit" disabled={create.isPending || !source.trim() || !target.trim()}>
          Add
        </Button>
        <div className="w-full">
          <ErrorText error={create.error} />
        </div>
      </form>
    </Card>
  )
}

function BulkImportForm({
  languageId,
  onDone,
}: {
  languageId: number
  onDone: () => void
}) {
  const bulk = useBulkImport(languageId)
  const [text, setText] = useState('')

  return (
    <Card>
      <p className="mb-2 text-sm text-muted">
        One pair per line. <code>=</code>, a comma, a tab or a dash all work as the
        separator. Lines starting with <code>#</code> are ignored.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        spellCheck={false}
        placeholder={'house = domus\ndog = canis\ncat = felis'}
        className="w-full rounded-lg border border-border-subtle bg-canvas px-3 py-2 font-mono text-sm
          text-ink placeholder:text-muted focus:border-brand focus:outline-none"
      />

      <div className="mt-3 flex items-center gap-2">
        <Button
          onClick={() => bulk.mutate(text, { onSuccess: () => setText('') })}
          disabled={bulk.isPending || !text.trim()}
        >
          {bulk.isPending ? 'Importing…' : 'Import'}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>

      <ErrorText error={bulk.error} />

      {bulk.data && (
        <div className="mt-3 space-y-1 text-sm">
          <p className="text-positive">
            Imported {bulk.data.created}
            {bulk.data.skipped_duplicates > 0 &&
              ` · skipped ${bulk.data.skipped_duplicates} duplicate${
                bulk.data.skipped_duplicates === 1 ? '' : 's'
              }`}
          </p>
          {bulk.data.errors.map((message) => (
            <p key={message} className="text-negative">
              {message}
            </p>
          ))}
        </div>
      )}
    </Card>
  )
}

function EntryTable({
  entries,
  total,
  languageId,
  editable,
  sourceLabel,
  targetLabel,
}: {
  entries: Entry[]
  total: number
  languageId: number
  editable: boolean
  sourceLabel: string
  targetLabel: string
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border-subtle">
      <table className="w-full text-sm">
        <thead className="bg-surface text-left text-xs tracking-wide text-muted uppercase">
          <tr>
            <th className="px-4 py-2 font-medium">{sourceLabel}</th>
            <th className="px-4 py-2 font-medium">{targetLabel}</th>
            {editable && <th className="w-24 px-4 py-2" />}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              languageId={languageId}
              editable={editable}
            />
          ))}
        </tbody>
      </table>

      {total > entries.length && (
        <p className="border-t border-border-subtle px-4 py-2 text-xs text-muted">
          Showing {entries.length} of {total}. Use search to narrow the list.
        </p>
      )}
    </div>
  )
}

function EntryRow({
  entry,
  languageId,
  editable,
}: {
  entry: Entry
  languageId: number
  editable: boolean
}) {
  const update = useUpdateEntry(languageId)
  const remove = useDeleteEntry(languageId)
  const [editing, setEditing] = useState(false)
  const [source, setSource] = useState(entry.source_text)
  const [target, setTarget] = useState(entry.target_text)

  function save() {
    update.mutate(
      { id: entry.id, source_text: source, target_text: target },
      { onSuccess: () => setEditing(false) },
    )
  }

  if (editing) {
    return (
      <tr className="border-t border-border-subtle bg-brand-soft/40">
        <td className="px-4 py-2">
          <Input value={source} onChange={(e) => setSource(e.target.value)} autoFocus />
        </td>
        <td className="px-4 py-2">
          <Input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
              if (e.key === 'Escape') setEditing(false)
            }}
          />
        </td>
        <td className="px-4 py-2">
          <div className="flex gap-1">
            <Button variant="ghost" onClick={save} disabled={update.isPending}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              ✕
            </Button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr className="border-t border-border-subtle hover:bg-surface">
      <td className="px-4 py-2">{entry.source_text}</td>
      <td className="px-4 py-2 font-medium">{entry.target_text}</td>
      {editable && (
        <td className="px-4 py-2">
          <div className="flex gap-1">
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button variant="danger" onClick={() => remove.mutate(entry.id)}>
              ✕
            </Button>
          </div>
        </td>
      )}
    </tr>
  )
}
